import { useState } from 'react';

import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Toaster as Sonner, type ToasterProps } from 'sonner';

import { useEffectiveDirection } from '@/lib/hooks/useEffectiveDirection';
import { selectResolvedTheme, useThemeStore } from '@/lib/store/themeStore';

/** Touch devices: the on-screen keyboard covers bottom toasts, so coarse
 *  pointers get top-center; fine-pointer desktops keep sonner's bottom-right. */
export const resolveToasterPosition = (coarsePointer: boolean): ToasterProps['position'] =>
  coarsePointer ? 'top-center' : 'bottom-right';

// Theme comes from the app's themeStore (Vite app, no next-themes); high-contrast maps to 'light'.
const Toaster = ({ ...props }: ToasterProps) => {
  const { t } = useTranslation();
  const resolvedTheme = useThemeStore(selectResolvedTheme);
  // Evaluated once at mount (SSR-safe): pointer class doesn't change mid-
  // session, and moving live toasts between corners would be worse than a
  // stale corner on the rare convertible flip.
  const [position] = useState<ToasterProps['position']>(() =>
    resolveToasterPosition(
      typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches
    )
  );
  // Mounts in App.tsx, outside AppShell's Direction.Provider — compute
  // directly so toasts follow the v4-only override, not just the ambient
  // locale direction sonner's own 'auto' default would fall back to.
  const direction = useEffectiveDirection();

  return (
    <Sonner
      theme={resolvedTheme === 'dark' ? 'dark' : 'light'}
      dir={direction}
      position={position}
      // The user closes a toast that covers page controls.
      closeButton
      className="toaster group"
      // P0-6: an open modal Radix dialog sets pointer-events:none on <body>,
      // which silently makes toast action buttons unclickable (e.g. the
      // refresh toast behind the exam Focus-Lost dialog). Toasts re-enable
      // their own pointer events.
      //
      // Error/warning tone hooks. Sonner honours its own --error-*/--warning-*
      // vars ONLY under `richColors` (gated on [data-rich-colors='true']), and
      // richColors ships a raw-HSL palette blind to our six theme scopes — so
      // the tone rides these classes plus the token island in
      // src/styles/index.css (§ SN1) instead.
      toastOptions={{
        className: 'pointer-events-auto',
        classNames: { error: 'ap-toast-error', warning: 'ap-toast-warning' },
        closeButtonAriaLabel: t('CommonText.close'),
      }}
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <OctagonXIcon className="size-4" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
          '--border-radius': 'var(--radius)',
        } as React.CSSProperties
      }
      {...props}
    />
  );
};

export { Toaster };
